import React, { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import { X, Check, Loader2, Folder, FolderOpen, Image as ImageIcon, Video, Play } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Iconos por tipo de lista de sistema
const SYSTEM_LIST_TYPES = new Set(["posts", "stories", "messages", "streams", "media_stickers"]);

/**
 * Modal para elegir media del vault de una creator.
 * Layout tipo Chatterly/OnlyFans: sidebar con carpetas + grid de miniaturas + multi-select.
 */
export default function VaultPickerModal({ creatorId, onClose, onPick }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Map()); // id -> media
  const [type, setType] = useState("");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null); // null = Todos
  const foldersLoadedRef = useRef(false);

  const load = async (nextOffset = 0, replace = true, folderId = activeFolder) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ creator_id: creatorId, offset: String(nextOffset) });
      if (type) params.set("media_type", type);
      if (folderId != null) params.set("list_id", String(folderId));
      const r = await axios.get(`${API}/chatterly/vault/media?${params.toString()}`, {
        withCredentials: true,
      });
      const vm = r.data.vaultMedia || r.data;
      const rawItems = vm.data || vm.list || [];

      // Extraer carpetas desde listStates la primera vez
      if (rawItems.length > 0 && !foldersLoadedRef.current) {
        const first = rawItems[0];
        const ls = Array.isArray(first.listStates) ? first.listStates : [];
        setFolders(ls);
        foldersLoadedRef.current = true;
      }

      const items = rawItems.map((m) => {
        const f = m.files || {};
        // Cadena de URLs a intentar en orden — cubre todos los tamaños/formatos posibles
        // (jpg de thumb/preview/squarePreview + `full` para GIF/originales sin resample).
        const candidates = [
          f.squarePreview?.url,
          f.preview?.url,
          f.thumb?.url,
          f.full?.url,
        ].filter(Boolean);
        // Cada candidato se envuelve con nuestro proxy backend (evita CORS + IP-lock)
        const proxied = candidates.map(
          (u) => `${API}/chatterly/media-proxy?creator_id=${encodeURIComponent(creatorId)}&url=${encodeURIComponent(u)}`
        );
        return {
          id: m.id,
          type: m.type,
          thumbCandidates: proxied,
          thumb: proxied[0] || null,
          full: f.full?.url || null,
          duration: m.duration,
          hasError: !!m.hasError,
          isReady: m.isReady !== false,
        };
      });
      setMedia((prev) => (replace ? items : [...prev, ...items]));
      setHasMore(!!vm.hasMore);
      setOffset(vm.newOffset || nextOffset + items.length);
    } catch (e) {
      console.error("vault load error", e);
    } finally {
      setLoading(false);
    }
  };

  // Recargar cuando cambia filtro type o folder
  useEffect(() => {
    setMedia([]);
    setOffset(0);
    load(0, true, activeFolder);
     
  }, [creatorId, type, activeFolder]);

  const toggle = (m) => {
    setSelected((prev) => {
      const n = new Map(prev);
      if (n.has(m.id)) n.delete(m.id);
      else n.set(m.id, m);
      return n;
    });
  };

  const handleConfirm = () => {
    onPick(Array.from(selected.values()));
  };

  const { systemFolders, customFolders } = useMemo(() => {
    const sys = [];
    const cust = [];
    for (const f of folders) {
      if (SYSTEM_LIST_TYPES.has(f.type)) sys.push(f);
      else cust.push(f);
    }
    // Orden alfabético para custom
    cust.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return { systemFolders: sys, customFolders: cust };
  }, [folders]);

  const renderFolderBtn = (id, name, Icon, count) => {
    const isActive = activeFolder === id || (id === null && activeFolder === null);
    return (
      <button
        key={id ?? "all"}
        onClick={() => setActiveFolder(id)}
        data-testid={`vault-folder-${id ?? "all"}`}
        className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-left text-xs transition ${
          isActive
            ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
            : "text-gray-400 hover:bg-white/5 hover:text-white border border-transparent"
        }`}
      >
        <Icon className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="flex-1 truncate">{name}</span>
        {count != null && <span className="text-[10px] opacity-60">{count}</span>}
      </button>
    );
  };

  return (
    <div
      data-testid="chatterly-vault-modal"
      className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-5xl h-[85vh] overflow-hidden bg-zinc-950 border border-emerald-500/20 rounded-2xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-white/5 flex items-center gap-3">
          <h3 className="text-white text-base font-bold flex-1">
            Vault · {selected.size > 0 ? `${selected.size} seleccionados` : "Selecciona archivos"}
          </h3>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="px-2 py-1 rounded bg-zinc-800 border border-white/10 text-white text-xs"
            data-testid="vault-filter-type"
          >
            <option value="">Todos los tipos</option>
            <option value="photo">Fotos</option>
            <option value="video">Videos</option>
            <option value="gif">GIFs</option>
          </select>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1" data-testid="vault-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body: sidebar + grid */}
        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar carpetas */}
          <aside className="w-52 flex-shrink-0 border-r border-white/5 overflow-y-auto p-2 space-y-1 bg-black/40">
            {renderFolderBtn(null, "Todos", FolderOpen)}
            {systemFolders.length > 0 && (
              <>
                <div className="text-[10px] uppercase tracking-widest text-gray-600 mt-3 mb-1 px-2">Sistema</div>
                {systemFolders.map((f) => renderFolderBtn(f.id, f.name, ImageIcon))}
              </>
            )}
            {customFolders.length > 0 && (
              <>
                <div className="text-[10px] uppercase tracking-widest text-gray-600 mt-3 mb-1 px-2">
                  Carpetas ({customFolders.length})
                </div>
                {customFolders.map((f) => renderFolderBtn(f.id, f.name, Folder))}
              </>
            )}
            {folders.length === 0 && !loading && (
              <div className="px-2 py-4 text-[11px] text-gray-500">Sin carpetas</div>
            )}
          </aside>

          {/* Grid de media */}
          <div className="flex-1 overflow-y-auto p-3">
            {loading && media.length === 0 ? (
              <div className="py-12 text-center text-gray-500">
                <Loader2 className="w-6 h-6 animate-spin mx-auto" />
              </div>
            ) : media.length === 0 ? (
              <div className="py-12 text-center text-gray-500 text-sm">
                Esta carpeta está vacía.
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {media.map((m) => {
                  const isSel = selected.has(m.id);
                  return (
                    <button
                      key={m.id}
                      onClick={() => toggle(m)}
                      data-testid={`chatterly-vault-item-${m.id}`}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition group ${
                        isSel ? "border-emerald-500 ring-2 ring-emerald-500/50" : "border-white/5 hover:border-white/20"
                      }`}
                    >
                      {m.thumb ? (
                        <>
                          <img
                            src={m.thumb}
                            alt=""
                            loading="lazy"
                            data-idx="0"
                            className="w-full h-full object-cover"
                            onLoad={(e) => {
                              e.target.dataset.loaded = "1";
                            }}
                            onError={(e) => {
                              // Intentar el siguiente candidato en la cadena
                              const idx = parseInt(e.target.dataset.idx || "0", 10);
                              const next = m.thumbCandidates?.[idx + 1];
                              if (next) {
                                e.target.dataset.idx = String(idx + 1);
                                e.target.src = next;
                              } else {
                                e.target.style.display = "none";
                                const ph = e.target.parentElement.querySelector('[data-placeholder]');
                                if (ph) ph.classList.remove('hidden');
                              }
                            }}
                          />
                          {/* Placeholder si la imagen no carga (URL de OF IP-locked) */}
                          <div
                            data-placeholder
                            className="hidden absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 flex flex-col items-center justify-center gap-1"
                          >
                            {m.type === "video" ? (
                              <Video className="w-7 h-7 text-emerald-500/70" />
                            ) : m.type === "gif" ? (
                              <ImageIcon className="w-7 h-7 text-purple-500/70" />
                            ) : (
                              <ImageIcon className="w-7 h-7 text-blue-400/70" />
                            )}
                            <span className="text-[9px] text-gray-500 uppercase tracking-wider">{m.type}</span>
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                          {m.type === "video" ? <Video className="w-6 h-6 text-gray-500" /> : <ImageIcon className="w-6 h-6 text-gray-500" />}
                        </div>
                      )}
                      {m.type === "video" && (
                        <span className="absolute bottom-1 left-1 flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded bg-black/70 text-white">
                          <Play className="w-2.5 h-2.5 fill-white" /> {m.duration || ""}
                        </span>
                      )}
                      {isSel && (
                        <div className="absolute top-1 right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {hasMore && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => load(offset, false)}
                  disabled={loading}
                  data-testid="vault-load-more"
                  className="text-xs px-3 py-1.5 rounded-md bg-white/5 text-emerald-400 hover:bg-white/10 disabled:opacity-50"
                >
                  {loading ? "Cargando…" : "Cargar más"}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-white/5 flex items-center justify-between bg-black/40">
          <span className="text-xs text-gray-400">
            {selected.size > 0 ? `${selected.size} archivo(s) listos para adjuntar` : "Selecciona uno o más archivos"}
          </span>
          <div className="flex items-center gap-2">
            {selected.size > 0 && (
              <button
                onClick={() => setSelected(new Map())}
                className="px-3 py-1.5 text-xs text-gray-400 hover:text-white"
              >
                Limpiar
              </button>
            )}
            <button
              onClick={handleConfirm}
              disabled={selected.size === 0}
              data-testid="chatterly-vault-confirm"
              className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs font-semibold hover:opacity-90 disabled:opacity-50"
            >
              Adjuntar {selected.size > 0 ? `(${selected.size})` : ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
